import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ConfigProvider, Layout } from "antd";

import { AuthProvider } from "./context/AuthContext";
import { ModalProvider } from "./context/ModalContext";
import { ChatProvider } from "./context/ChatContext";

import ProtectedRoute from "./components/security/ProtectedRoute";

import TagManager from "react-gtm-module";

// Layouts - Load immediately (used by all routes)
import FrontPageLayout from "./components/layouts/FrontpageLayout";
import MainLayout from "./components/layouts/MainLayout";
import AdminLayout from "./components/layouts/AdminLayout";
import ScrollToTop from "./components/common/ScrollToTop";

// Lazy load all page components
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const AuctionPage = lazy(() => import("./pages/AuctionPage"));
const AuctionListPage = lazy(() => import("./pages/AuctionListPage"));
const AuctionCategoryPage = lazy(() => import("./pages/AuctionCategoryPage"));
const NakyvyysPage = lazy(() => import("./pages/NakyvyysPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ResalePage = lazy(() => import("./pages/ResalePage"));
const ResalePaymentReturnPage = lazy(() => import("./pages/ResalePaymentReturnPage"));
const UserConfirmPage = lazy(() => import("./pages/UserConfirmPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PublicBlogList = lazy(() => import("./pages/PublicBlogList"));
const KauppakirjapalveluPage = lazy(() => import("./pages/KauppakirjapalveluPage"));
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages - Lazy loaded (heavy pages)
const Profile = lazy(() => import("./pages/admin/Profile"));
const Auctions = lazy(() => import("./pages/admin/Auctions"));
const UserAuctions = lazy(() => import("./pages/admin/UserAuctions"));
const Auction = lazy(() => import("./pages/admin/Auction"));
const Chats = lazy(() => import("./pages/admin/Chats"));
const Gallery = lazy(() => import("./pages/admin/Gallery"));
const Properties = lazy(() => import("./pages/admin/Properties"));
const PropertyEdit = lazy(() => import("./pages/admin/PropertyEdit"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const Users = lazy(() => import("./pages/admin/Users"));
const User = lazy(() => import("./pages/admin/User"));
const DiscountCodes = lazy(() => import("./pages/admin/DiscountCodes"));
const Products = lazy(() => import("./pages/admin/Products"));
const Blog = lazy(() => import("./pages/admin/Blog"));
const BlogEdit = lazy(() => import("./pages/admin/BlogEdit"));
const Offers = lazy(() => import("./pages/admin/Offers"));
const Areas = lazy(() => import("./pages/admin/Areas"));
const Payments = lazy(() => import("./pages/admin/Payments"));
const AreasEdit = lazy(() => import("./pages/admin/AreasEdit"));
const SettingsEdit = lazy(() => import("./pages/admin/SettingsEdit"));
const ContactMessagesPage = lazy(() => import("./pages/admin/ContactMessagesPage"));
const Leads = lazy(() => import("./pages/admin/Leads"));
const AdminActions = lazy(() => import("./pages/admin/AdminActions"));
const Contracts = lazy(() => import("./pages/admin/Contracts"));
const ContractEdit = lazy(() => import("./pages/admin/ContractEdit"));
const Pages = lazy(() => import("./pages/admin/Pages"));
const PageEdit = lazy(() => import("./pages/admin/PageEdit"));
const ResaleOrders = lazy(() => import("./pages/admin/ResaleOrders"));
const Luonnokset = lazy(() => import("./pages/admin/Luonnokset"));
const Puhelinmyynti = lazy(() => import("./pages/admin/Puhelinmyynti"));
const PuhelinmyyntiOpas = lazy(() => import("./pages/admin/PuhelinmyyntiOpas"));
const PuhelinmyyntiSeuranta = lazy(() => import("./pages/admin/PuhelinmyyntiSeuranta"));

// Auth components
const AuthReturn = lazy(() => import("./components/security/AuthReturn"));
const Contract = lazy(() => import("./pages/admin/Contract"));
const PaymentReturn = lazy(() => import("./pages/admin/PaymentReturn"));

const LoadingFallback = () => (
    <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
);

const App = () => {
    const routes = [
        {
            path: "/",
            element: (
                <FrontPageLayout>
                    <Home />
                </FrontPageLayout>
            ),
        },
        {
            path: "/404",
            element: (
                <MainLayout>
                    <NotFound />
                </MainLayout>
            ),
        },
        {
            path: "/kirjaudu",
            element: (
                <MainLayout>
                    <Login />
                </MainLayout>
            ),
        },
        {
            path: "/kohteet",
            element: (
                <MainLayout>
                    <AuctionListPage />
                </MainLayout>
            ),
        },
        {
            path: "/kohteet/:type",
            element: (
                <MainLayout>
                    <AuctionListPage />
                </MainLayout>
            ),
        },
        {
            path: "/kohde/:number",
            element: (
                <MainLayout>
                    <AuctionPage />
                </MainLayout>
            ),
        },
        {
            path: "/kohde/:number/:slug",
            element: (
                <MainLayout>
                    <AuctionPage />
                </MainLayout>
            ),
        },
        {
            path: "/:typeslug/a?/:areaslug?",
            element: (
                <MainLayout>
                    <AuctionCategoryPage auctionType="rent" />
                </MainLayout>
            ),
        },
        {
            path: "/:typeslug/a?/:areaslug?/v?/:weekslug?",
            element: (
                <MainLayout>
                    <AuctionCategoryPage auctionType="rent" />
                </MainLayout>
            ),
        },
        {
            path: "/:typeslug/v?/:weekslug?",
            element: (
                <MainLayout>
                    <AuctionCategoryPage auctionType="rent" />
                </MainLayout>
            ),
        },
        {
            /* Näkyvyyssivu (Mika 6.10.2026): kaikki näkyvyystuotteet ja osto omalta tililtä. */
            path: "/nakyvyys",
            element: (
                <MainLayout>
                    <NakyvyysPage />
                </MainLayout>
            ),
        },
        {
            path: "/yhteystiedot",
            element: (
                <MainLayout breadcrumbsVisible={false}>
                    <ContactPage />
                </MainLayout>
            ),
        },
        {
            path: "/tarjous",
            element: (
                <MainLayout breadcrumbsVisible={false}>
                    <ResalePage />
                </MainLayout>
            ),
        },
        {
            path: "/tarjous/maksu/:result",
            element: (
                <MainLayout breadcrumbsVisible={false}>
                    <ResalePaymentReturnPage />
                </MainLayout>
            ),
        },
        {
            path: "/ehdot-ja-ohjeet",
            element: (
                <MainLayout breadcrumbsVisible={false}>
                    <FaqPage />
                </MainLayout>
            ),
        },
        {
            path: "/ehdot-ja-ohjeet/:page",
            element: (
                <MainLayout breadcrumbsVisible={false}>
                    <FaqPage />
                </MainLayout>
            ),
        },
        {
            path: "/vahvista/:uuid",
            element: (
                <MainLayout>
                    <UserConfirmPage />
                </MainLayout>
            ),
        },
        {
            path: "/vaihda-salasana/:token",
            element: (
                <MainLayout>
                    <PasswordResetPage />
                </MainLayout>
            ),
        },
        {
            path: "/kauppakirjapalvelu",
            element: (
                <MainLayout>
                    <KauppakirjapalveluPage />
                </MainLayout>
            ),
        },
        {
            path: "/artikkelit",
            element: (
                <MainLayout>
                    <PublicBlogList />
                </MainLayout>
            ),
        },
        {
            path: "/artikkelit/:slug",
            element: (
                <MainLayout>
                    <BlogPost />
                </MainLayout>
            ),
        },
        {
            element: <ProtectedRoute requiredRole="ROLE_USER" />,
            children: [
                {
                    path: "/hallinta/ilmoitukset",
                    element: (
                        <AdminLayout>
                            <Auctions />
                        </AdminLayout>
                    ),
                },
            ],
        },
        {
            element: <ProtectedRoute requiredRole="ROLE_USER" />,
            children: [
                {
                    path: "/oma-sivu",
                    element: (
                        <AdminLayout>
                            <Profile />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/viestit",
                    element: (
                        <AdminLayout>
                            <Chats />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/viestit/:chatUuid",
                    element: (
                        <AdminLayout>
                            <Chats />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/ilmoitukset",
                    element: (
                        <AdminLayout>
                            <UserAuctions />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/ilmoitukset/:uuid",
                    element: (
                        <AdminLayout>
                            <Auction />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/sopimus/:uuid",
                    element: (
                        <AdminLayout>
                            <Contract />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/kuvapankki",
                    element: (
                        <AdminLayout>
                            <Gallery />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/oma-sivu/kuvapankki/:folderId",
                    element: (
                        <AdminLayout>
                            <Gallery />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/auth/return",
                    element: <AuthReturn />,
                },
                {
                    path: "/maksu/:result",
                    element: (
                        <AdminLayout sidebar={false}>
                            <PaymentReturn />
                        </AdminLayout>
                    ),
                },
            ],
        },
        {
            element: <ProtectedRoute requiredRole="ROLE_PUHELINMYYJA" />,
            children: [
                {
                    path: "/hallinta/puhelinmyynti",
                    element: (
                        <AdminLayout>
                            <Puhelinmyynti />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/myyjan-opas",
                    element: (
                        <AdminLayout>
                            <PuhelinmyyntiOpas />
                        </AdminLayout>
                    ),
                },
            ],
        },
        {
            element: <ProtectedRoute requiredRole="ROLE_SEO" />,
            children: [
                {
                    path: "/hallinta/alueet",
                    element: (
                        <AdminLayout>
                            <Areas />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/alueet/:areaId",
                    element: (
                        <AdminLayout>
                            <AreasEdit />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/kohteet",
                    element: (
                        <AdminLayout>
                            <Properties />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/kohteet/muokkaa/:id",
                    element: (
                        <AdminLayout>
                            <PropertyEdit />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/artikkelit",
                    element: (
                        <AdminLayout>
                            <Blog />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/artikkelit/:postId",
                    element: (
                        <AdminLayout>
                            <BlogEdit />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/asetukset",
                    element: (
                        <AdminLayout>
                            <Settings />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/asetukset/:settingId",
                    element: (
                        <AdminLayout>
                            <SettingsEdit />
                        </AdminLayout>
                    ),
                },
            ],
        },
        {
            element: <ProtectedRoute requiredRole="ROLE_SUPERADMIN" />,
            children: [
                {
                    path: "/hallinta/puhelinmyynti-seuranta",
                    element: (
                        <AdminLayout>
                            <PuhelinmyyntiSeuranta />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/luonnokset",
                    element: (
                        <AdminLayout>
                            <Luonnokset />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/kayttajat",
                    element: (
                        <AdminLayout>
                            <Users />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/kayttajat/:id",
                    element: (
                        <AdminLayout>
                            <User />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/kohteet/uusi",
                    element: (
                        <AdminLayout>
                            <PropertyEdit />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/alennuskoodit",
                    element: (
                        <AdminLayout>
                            <DiscountCodes />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/ilmoitukset",
                    element: (
                        <AdminLayout>
                            <Auctions />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/yhteysviestit",
                    element: (
                        <AdminLayout>
                            <ContactMessagesPage />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/liidit",
                    element: (
                        <AdminLayout>
                            <Leads />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/sisaltosivut",
                    element: (
                        <AdminLayout>
                            <Pages />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/sisaltosivut/:pageId",
                    element: (
                        <AdminLayout>
                            <PageEdit />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/tuotteet",
                    element: (
                        <AdminLayout>
                            <Products />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/tarjoukset",
                    element: (
                        <AdminLayout>
                            <Offers />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/maksusuoritukset",
                    element: (
                        <AdminLayout>
                            <Payments />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/tehomyyntitilaukset",
                    element: (
                        <AdminLayout>
                            <ResaleOrders />
                        </AdminLayout>
                    ),
                },
                {
                    // Vanha osoite jaa toimimaan, jotta kirjanmerkit eivat katkea.
                    path: "/hallinta/jalleenmyyntitilaukset",
                    element: (
                        <AdminLayout>
                            <ResaleOrders />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/sopimukset",
                    element: (
                        <AdminLayout>
                            <Contracts />
                        </AdminLayout>
                    ),
                },
                {
                    path: "/hallinta/sopimukset/:auctionUuid/muokkaa",
                    element: (
                        <AdminLayout>
                            <ContractEdit />
                        </AdminLayout>
                    ),
                },
            ],
        },
        {
            element: <ProtectedRoute requiredRole="ROLE_GOD" />,
            children: [
                {
                    path: "/hallinta/toiminnot",
                    element: (
                        <AdminLayout>
                            <AdminActions />
                        </AdminLayout>
                    ),
                },
            ],
        },
    ];

    useEffect(() => {
        const tagManagerArgs = {
            gtmId: "AW-11328598452",
        };
        TagManager.initialize(tagManagerArgs);
    }, []);

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: "#01696e",
                    colorInfo: "#01696e",
                    colorWarning: "#dabe1f",
                    colorSuccess: "#62a93e",
                    colorLink: "#2f54eb",
                    borderRadius: 4,
                    colorError: "#e13235",
                    wireframe: false,
                },
                components: {
                    Radio: {
                        buttonSolidCheckedBg: "#00b96b",
                        buttonSolidCheckedHoverBg: "#00b96b",
                    },
                },
            }}
        >
            <ModalProvider>
                <Router>
                    <ScrollToTop />
                    <AuthProvider>
                        <ChatProvider>
                            <Layout className="bg-white font-Outfit">
                                <Suspense fallback={<LoadingFallback />}>
                                    <Routes>
                                        {routes.map((route, index) => {
                                            if (route.children) {
                                                return (
                                                    <Route key={index} element={route.element}>
                                                        {route.children.map((child, index) => (
                                                            <Route
                                                                key={index}
                                                                path={child.path}
                                                                element={child.element}
                                                            />
                                                        ))}
                                                    </Route>
                                                );
                                            } else {
                                                return <Route key={index} path={route.path} element={route.element} />;
                                            }
                                        })}
                                    </Routes>
                                </Suspense>
                            </Layout>
                        </ChatProvider>
                    </AuthProvider>
                </Router>
            </ModalProvider>
        </ConfigProvider>
    );
};

export default App;
